import { z } from 'zod';

import { auditSchema, timestampSchema } from './common';
import { phaseIdSchema, tournamentIdSchema } from './ids';
import { tournamentSettingsSchema } from './settings';

/**
 * `setup`        — Einstellungen werden gepflegt, noch keine Anmeldung
 * `registration` — Kapitäne melden Teams an
 * `running`      — Turnier läuft, Einstellungen sind gesperrt
 * `paused`       — vorübergehend angehalten, keine Ergebnismeldung möglich
 * `completed`    — Sieger steht fest
 * `archived`     — abgeschlossenes Turnier vergangener Jahre
 */
export const tournamentStatusSchema = z.enum([
  'setup',
  'registration',
  'running',
  'paused',
  'completed',
  'archived',
]);

export type TournamentStatus = z.infer<typeof tournamentStatusSchema>;

/**
 * The tournament document at `tournaments/{tournamentId}`.
 *
 * Settings are embedded rather than kept in a sibling document: they are always
 * needed together with the tournament, they are small, and a single document
 * means a single atomic write when the director saves.
 */
export const tournamentSchema = z.object({
  id: tournamentIdSchema,
  settings: tournamentSettingsSchema,
  status: tournamentStatusSchema,
  /** Phase currently being played; `null` before the start and after the end. */
  currentPhaseId: phaseIdSchema.nullable(),
  startedAt: timestampSchema.nullable(),
  completedAt: timestampSchema.nullable(),
  ...auditSchema.shape,
});

export type Tournament = z.infer<typeof tournamentSchema>;

/** Settings may only be changed while the tournament has not started yet. */
export function areSettingsEditable(status: TournamentStatus): boolean {
  return status === 'setup' || status === 'registration';
}

/**
 * Whether captains may register a team right now.
 *
 * Both conditions must hold, exactly as the `registerTeam` function checks them
 * server-side: the status opens the window, the setting is the switch inside
 * it. Asking this one question everywhere keeps the app from advertising a
 * registration that the server would refuse.
 */
export function isRegistrationOpen(tournament: Tournament): boolean {
  return tournament.status === 'registration' && tournament.settings.registration.isOpen;
}
